import { useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { leilaineSorrindo } from "@/lib/images";
import { Picture } from "@/components/landing/Picture";
import { CheckCircle2, Calendar, Mail, Phone, Users, ArrowRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/landing/Footer";
import { trackPurchase, trackPaymentClick } from "@/lib/analytics";

const CHECKOUT_URL = "https://www.asaas.com/c/qtylw84bfxj1rd5e";

export const Route = createFileRoute("/obrigado")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
  }),
  head: () => ({
    meta: [
      { title: "Inscrição confirmada | SPRINT CODE" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content:
          "Sua inscrição no SPRINT CODE foi recebida. Confirme pelo link de pagamento e agende a conversa de 20 minutos para definir a agenda das suas 4 sessões.",
      },
      { property: "og:title", content: "Inscrição confirmada — SPRINT CODE" },
      { property: "og:url", content: "https://sprint.campioto.com/obrigado" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Inscrição confirmada — SPRINT CODE" },
      {
        property: "og:description",
        content:
          "Seu programa está a um passo. Confirme o pagamento e agende sua conversa de 20 minutos com a Leilaine para definir a agenda das 4 sessões.",
      },
      {
        name: "twitter:description",
        content:
          "Seu programa está a um passo. Confirme o pagamento e agende sua conversa de 20 minutos com a Leilaine para definir a agenda das 4 sessões.",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "SPRINT CODE",
              item: "https://sprint.campioto.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Inscrição confirmada",
              item: "https://sprint.campioto.com/obrigado",
            },
          ],
        }),
      },
    ],
    links: [{ rel: "canonical", href: "https://sprint.campioto.com/obrigado" }],
  }),
  component: ObrigadoPage,
});

const steps = [
  {
    icon: Mail,
    title: "Confirmação por e-mail",
    desc: "Você receberá os detalhes da sua inscrição no e-mail cadastrado.",
  },
  {
    icon: Calendar,
    title: "Agenda das 4 sessões",
    desc: "Nada de data fixa: as 4 sessões de 1h30 são marcadas com você, no seu ritmo.",
  },
  {
    icon: Phone,
    title: "Conversa de 20 minutos",
    desc: "Após o pagamento, você agenda uma call comigo para definirmos a agenda e o foco de cada sessão.",
  },
];

function ObrigadoPage() {
  useEffect(() => {
    trackPurchase({ content_name: "ZNITH AI SPRINT — programa de 4 sessões" });
  }, []);

  return (
    <main className="relative min-h-screen overflow-hidden bg-midnight text-ivory">
      <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_top,_color-mix(in_oklab,_var(--gold)_8%,_transparent)_0%,_transparent_60%)]" />

      {/* Foto decorativa de fundo — desktop */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-0 z-0 hidden h-screen w-[45%] md:block"
      >
        <Picture
          pic={leilaineSorrindo}
          alt=""
          decorative
          sizes="(min-width: 768px) 45vw, 1px"
          loading="eager"
          className="h-full w-full object-cover object-top"
          style={{
            opacity: 0.4,
            maskImage:
              "linear-gradient(to left, black 40%, transparent 88%), linear-gradient(to top, black 55%, transparent 96%)",
            maskComposite: "intersect",
            WebkitMaskImage:
              "linear-gradient(to left, black 40%, transparent 88%), linear-gradient(to top, black 55%, transparent 96%)",
            WebkitMaskComposite: "source-in",
          }}
        />
      </div>

      {/* Foto decorativa de fundo — celular */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[55vh] md:hidden"
      >
        <Picture
          pic={leilaineSorrindo}
          alt=""
          decorative
          sizes="(min-width: 768px) 1px, 100vw"
          loading="eager"
          className="h-full w-full object-cover object-top"
          style={{
            opacity: 0.22,
            maskImage: "linear-gradient(to bottom, black 25%, transparent 92%)",
            WebkitMaskImage: "linear-gradient(to bottom, black 25%, transparent 92%)",
          }}
        />
      </div>

      <div className="container-narrow relative z-10 flex min-h-screen flex-col">
        <header className="py-8">
          <Link
            to="/"
            className="text-xl tracking-[0.4em] text-ivory"
            style={{ fontFamily: "var(--font-display)" }}
          >
            ZNITH
          </Link>
        </header>

        <section className="flex flex-1 flex-col items-center justify-center py-12 text-center">
          <div className="flex w-full flex-col items-center text-center md:-translate-x-[10%]">
            <div className="mb-8 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-gold shadow-elegant">
              <CheckCircle2 className="h-10 w-10 text-primary-foreground" strokeWidth={2} />
            </div>

            <p className="mb-4 text-[11px] uppercase tracking-[0.4em] text-gold">
              Inscrição recebida
            </p>
            <h1 className="max-w-3xl text-balance text-4xl leading-tight md:text-6xl">
              Seu programa está a um passo.
            </h1>
            <p className="mt-6 max-w-xl text-pretty text-slate-muted md:text-lg">
              Você acabou de tomar uma das decisões mais lúcidas que pode tomar
              agora: priorizar clareza executiva sobre IA antes da maioria do
              mercado.
            </p>
            <p className="mt-5 text-sm italic text-slate-muted">
              Nos vemos na nossa conversa de 20 minutos. — Leilaine Campioto
            </p>
          </div>

          <div className="deferred-section cv-thanks-details mt-12 w-full max-w-2xl">
            <div className="glass-card rounded-3xl border border-gold/30 p-8 text-left shadow-elegant md:p-10">
              <p className="mb-3 text-[11px] uppercase tracking-[0.4em] text-gold">
                Próximo passo · Confirmar vaga
              </p>
              <h2 className="text-2xl leading-tight text-ivory md:text-3xl">
                Seu programa só é confirmado após o pagamento.
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-muted md:text-base">
                Clique abaixo para acessar o link seguro de pagamento e
                confirmar definitivamente o seu programa.
              </p>
              <Button
                asChild
                size="lg"
                className="mt-6 h-14 w-full bg-gradient-gold px-8 text-base text-primary-foreground hover:opacity-90 sm:w-auto"
              >
                <a
                  href={CHECKOUT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackPaymentClick()}
                >
                  Ir para o pagamento <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <p className="mt-3 text-sm text-slate-muted">
                Prefere Pix? R$ 6.870 à vista — chave znith@campioto.com
              </p>
              <p className="mt-4 inline-flex items-center gap-2 text-xs text-slate-muted">
                <Lock className="h-3.5 w-3.5 text-gold" />
                Link seguro · pagamento processado pelo Asaas
              </p>
              <p className="mt-3 text-xs text-slate-muted">
                Logo após o pagamento você recebe o link para agendar sua conversa de 20 minutos com a Leilaine.
              </p>
            </div>
          </div>
          <div className="deferred-section cv-thanks-details mx-auto mt-14 grid w-full max-w-3xl gap-4 sm:grid-cols-3">
            {steps.map((s) => (
              <div
                key={s.title}
                className="glass-card rounded-2xl p-6 text-left"
              >
                <s.icon className="mb-3 h-5 w-5 text-gold" />
                <h2 className="text-base font-semibold text-ivory">{s.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-muted">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="deferred-section cv-thanks-details mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm text-slate-muted">
            <span className="inline-flex items-center gap-2">
               <Calendar className="h-4 w-4 text-gold" /> 4 sessões de 1h30 · agenda combinada
            </span>
            <span className="inline-flex items-center gap-2">
              <Users className="h-4 w-4 text-gold" /> Online · individual ou grupo fechado
            </span>
          </div>

          <div className="deferred-section cv-thanks-details">
            <Button
              asChild
              variant="outline"
              className="mt-12 border-gold/40 bg-transparent text-ivory hover:bg-gold/10 hover:text-gold-soft"
            >
              <Link to="/">Voltar à página do programa</Link>
            </Button>
          </div>
        </section>

        <div className="deferred-section cv-thanks-footer"><Footer /></div>
      </div>
    </main>
  );
}
